import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import toast from 'react-hot-toast';
import { useParameterizedQuery } from 'react-fetching-library';

import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';

import PDFPreviewModal from '../../employee/order/order-details/tabs/DocumentAndNotificationsTab/PDFPreviewModal';
import { getClientDocument } from '../../../services/request';
import { extractYearFromOrderIdentifier } from '../../../services/functions';

export const AgreementsCard = ({ orderData }) => {
  const agreements = [
    {
      id: orderData.id,
      type: 'Umowa',
      number: orderData.agreement_number,
      isSigned: orderData.agreement_is_signed,
      signingDate: orderData.agreement_signing_date,
      signingDueDate: orderData.agreement_signing_due_date,
      isAgreement: true
    },

    ...(orderData.order_annexes || []).map(
      annex => ({
        id: annex.id,
        type: `Aneks #${annex.number}`,
        number: annex.number,
        isSigned: annex.is_signed,
        signingDate: annex.signing_date,
        signingDueDate: annex.signing_due_date,
        isAgreement: false
      })
    )
  ];

  const [previewUrl, setPreviewUrl] = React.useState(null);
  const { query, loading } = useParameterizedQuery(getClientDocument);

  const handleDownloadDocument = async (id, fileName, isAgreement = true, annexId = false, preview = false) => {
    const params = { order_id: orderData.id, template_name: getTemplateName(isAgreement) };
    if (!isAgreement) {
      params.annex_id = annexId;
    }
    const { payload, error, loading } = await query(id, params);
    if (!error && !loading) {
      if (preview) {
        setPreviewUrl(window.URL.createObjectURL(payload));
      } else {
        const url = window.URL.createObjectURL(payload);
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style = 'display: none';
        a.href = url;
        a.download = `${fileName}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }

    } else {
      toast.error('Nie udało się pobrać pliku');
    }
  };

  const getTemplateName = isAgreement => {
    let templateName = '';
    if (isAgreement) {
      if (orderData.use_contract_payer_data) {
        templateName = `agreement_zs_payer_${extractYearFromOrderIdentifier(orderData.identifier)}_signed`;
      } else {
        templateName = `agreement_zs_${extractYearFromOrderIdentifier(orderData.identifier)}_signed`;
      }
    } else {
      templateName = `agreement_annex_zs_${extractYearFromOrderIdentifier(orderData.identifier)}_signed`;
    }

    return templateName;
  };

  return (
    <Card
      sx={{
        borderRadius: 4,
        boxShadow: theme => theme.shadows[2]
      }}
    >
      <CardContent sx={{ p: 5 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          mb={4}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: 'primary.lighter',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <DescriptionOutlinedIcon color="primary" />
          </Box>

          <Typography
            variant="h4"
            fontWeight={700}
          >
            Umowy i aneksy
          </Typography>
        </Stack>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Typ</strong>
                </TableCell>

                <TableCell>
                  <strong>Numer</strong>
                </TableCell>

                <TableCell>
                  <strong>Status</strong>
                </TableCell>

                <TableCell>
                  <strong>Termin podpisania</strong>
                </TableCell>

                <TableCell>
                  <strong>Data podpisania</strong>
                </TableCell>

                <TableCell align="right">
                  <strong>Akcje</strong>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {agreements.map(row => (
                <TableRow key={`${row.type}-${row.id}`}>
                  <TableCell>
                    {row.type}
                  </TableCell>

                  <TableCell>
                    {row.number || '-'}
                  </TableCell>

                  {orderData.state === 'CON' && <TableCell>
                    <Chip
                      size="small"
                      color={
                        row.isSigned
                          ? 'success'
                          : 'warning'
                      }
                      label={
                        row.isSigned
                          ? 'Podpisany'
                          : 'Oczekuje na podpis'
                      }
                    />
                  </TableCell>}

                  <TableCell>
                    {row.signingDueDate
                      ? new Date(
                        row.signingDueDate
                      ).toLocaleDateString(
                        'pl-PL'
                      )
                      : '-'}
                  </TableCell>

                  <TableCell>
                    {row.signingDate
                      ? new Date(
                        row.signingDate
                      ).toLocaleDateString(
                        'pl-PL'
                      )
                      : '-'}
                  </TableCell>

                  <TableCell align="right">
                    {row.isSigned ? (
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                      >
                        <Tooltip title="Podgląd">
                          <span>
                            <IconButton
                              color="primary"
                              size="small"
                              disabled={loading}
                              onClick={() => handleDownloadDocument(
                                'agreement_zs_2026',
                                row.isAgreement ? `Umowa nr ${orderData.agreement_number}` : `Aneks nr ${row.number} do umowy ${orderData.agreement_number}`,
                                row.isAgreement,
                                row.id,
                                true
                              )
                              }
                            >
                              {loading ? (
                                <CircularProgress size={20} />
                              ) : (
                                <VisibilityOutlinedIcon />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Pobierz">
                          <span>
                            <IconButton
                              color="primary"
                              size="small"
                              disabled={loading}
                              onClick={() => handleDownloadDocument(
                                'agreement_zs_2026',
                                row.isAgreement ? `Umowa nr ${orderData.agreement_number}` : `Aneks nr ${row.number} do umowy ${orderData.agreement_number}`,
                                row.isAgreement,
                                row.id,
                                false
                              )
                              }
                            >
                              {loading ? (
                                <CircularProgress size={20} />
                              ) : (
                                <DownloadOutlinedIcon />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        Niedostępny
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}

              {!agreements.length && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                  >
                    Brak dokumentów
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
      {previewUrl !== null ? <PDFPreviewModal url={previewUrl} handleClose={() => setPreviewUrl(null)}/> : null}
    </Card>
  );
};
